/**
 * Reassembles one barcode out of the several ways a keyboard-wedge scanner can deliver it.
 *
 * A Zebra handheld running DataWedge's Keystroke Output plugin does not have a single wire
 * format. Depending on how the device's profile is configured the same trigger pull arrives as:
 *
 *   - one bulk string committed into whatever field has focus (the factory default: "Send
 *     Characters as Events" is off, so the data is delivered as a string);
 *   - a run of individual key events on the document ("Send Characters as Events" on, which is
 *     what Zebra recommends for web apps because it needs no focused field);
 *   - a mixture — printable characters as key events, the GS separators still as a string,
 *     because "Send Control Characters as Events" is a separate switch that defaults to off;
 *   - a clipboard paste, which is how a handful of third-party wedges and the Enterprise
 *     Browser behave.
 *
 * and it may or may not be closed by an Enter or a Tab, since the action key character defaults
 * to "None" and Basic Data Formatting is off out of the box.
 *
 * So this class takes every source at once, keeps them side by side, and at the end of the burst
 * picks the reading that carries the most information — which is how a GS separator survives
 * even when only one of the two channels transmitted it. It has no DOM dependencies so the
 * timing rules can be tested directly; `useHardwareScanner` supplies the events.
 */

export type WedgeSource = 'keys' | 'field' | 'paste';

export type WedgeScan = {
  /** The barcode, exactly as the scanner sent it. Interpretation happens in `gs1.ts`. */
  data: string;
  /** Which delivery channel carried the winning reading. */
  source: WedgeSource;
  /** What closed the burst. `quiet` means nothing did and the burst simply went idle. */
  terminator: 'enter' | 'tab' | 'quiet' | 'atomic';
  /** First character to emit, in ms. Zero for an atomic delivery. */
  durationMs: number;
  /** True when the winning reading contains at least one GS — used for operator diagnostics. */
  hasSeparators: boolean;
};

/** Why a burst was not turned into a scan, or null when it was. */
export type WedgeRejection = 'empty' | 'too-short' | 'duplicate' | null;

/**
 * Every burst the assembler saw, accepted or not. Exists so "I pulled the trigger and nothing
 * happened" is answerable on the device: a burst that was dropped is invisible without it.
 */
export type WedgeBurstResult = {
  data: string;
  source: WedgeSource;
  terminator: WedgeScan['terminator'];
  rejected: WedgeRejection;
};

export type WedgeAssemblerOptions = {
  onScan: (scan: WedgeScan) => void;
  /** Called for every burst, including the ones that produced no scan. Diagnostics only. */
  onBurstResult?: (result: WedgeBurstResult) => void;
  /**
   * Called at the end of every burst, whether or not it produced a scan. The host uses this to
   * empty the capture field — a burst that was rejected as too short or as a duplicate would
   * otherwise leave its characters sitting there for the next one to be appended to.
   */
  onBurstEnd?: () => void;
  /**
   * Idle time that ends a burst with no terminator. Long enough to ride out DataWedge's
   * inter-character delay (0 ms by default, and raised to single-digit ms on the profiles Zebra
   * recommends for web apps), short enough that a pick feels instant.
   */
  quietPeriodMs?: number;
  /**
   * Shortest burst accepted without a terminator; a terminator accepts any non-empty burst. Two
   * rather than something safer-sounding because zone rack labels really are printed as `A1`,
   * and DataWedge sends no terminator unless its action key character has been configured.
   */
  minLength?: number;
  /** Identical payloads inside this window are one trigger pull, not two. */
  duplicateWindowMs?: number;
  now?: () => number;
  schedule?: (fn: () => void, ms: number) => number;
  cancel?: (handle: number) => void;
};

const CONTROL_CHARACTERS = /[\u0000-\u001f]/g;

const withoutControls = (value: string) => value.replace(CONTROL_CHARACTERS, '');

/**
 * Picks the reading that carries the most barcode. Longest payload once control characters are
 * discounted wins — that is the channel that did not truncate — and a tie goes to whichever
 * reading still has its separators, because those are what the GS1 parser needs.
 */
const chooseBestReading = (
  candidates: Array<{ value: string; source: WedgeSource }>
): { value: string; source: WedgeSource } | null => {
  let best: { value: string; source: WedgeSource } | null = null;
  let bestDataLength = -1;
  let bestControlCount = -1;

  for (const candidate of candidates) {
    if (!candidate.value) continue;
    const dataLength = withoutControls(candidate.value).length;
    const controlCount = candidate.value.length - dataLength;
    if (
      dataLength > bestDataLength ||
      (dataLength === bestDataLength && controlCount > bestControlCount)
    ) {
      best = candidate;
      bestDataLength = dataLength;
      bestControlCount = controlCount;
    }
  }

  return best;
};

export class WedgeAssembler {
  private readonly options: Required<
    Pick<WedgeAssemblerOptions, 'quietPeriodMs' | 'minLength' | 'duplicateWindowMs'>
  > &
    WedgeAssemblerOptions;

  /** Characters seen as individual key events, control characters decoded back to their bytes. */
  private keys = '';

  /** Latest full value of the hidden capture field. */
  private fieldValue = '';

  /** Text the capture field reported through `beforeinput`, which survives value sanitisation. */
  private fieldInserts = '';

  private startedAt = 0;

  private timer: number | null = null;

  /**
   * Last payload emitted, with control characters discounted. Stripped because the same trigger
   * pull can arrive twice with different separator fidelity — the key channel flushing on the
   * quiet timer just before the field channel's events land — and those are one scan, not two.
   */
  private lastEmittedKey = '';

  private lastEmittedAt = -Infinity;

  constructor(options: WedgeAssemblerOptions) {
    this.options = {
      quietPeriodMs: 90,
      minLength: 2,
      duplicateWindowMs: 600,
      ...options
    };
  }

  private now() {
    return this.options.now ? this.options.now() : Date.now();
  }

  private arm() {
    const schedule = this.options.schedule || ((fn, ms) => window.setTimeout(fn, ms));
    const cancel = this.options.cancel || ((handle: number) => window.clearTimeout(handle));
    if (this.timer !== null) cancel(this.timer);
    this.timer = schedule(() => {
      this.timer = null;
      this.flush('quiet');
    }, this.options.quietPeriodMs);
  }

  private touch() {
    if (!this.startedAt) this.startedAt = this.now();
    this.arm();
  }

  /** True while a burst is in flight, so the host can swallow the key that ends it. */
  get isAssembling() {
    return Boolean(this.keys || this.fieldValue || this.fieldInserts);
  }

  /** A printable character, or a control character already decoded from its key combination. */
  pushKey(character: string) {
    if (!character) return;
    this.keys += character;
    this.touch();
  }

  /** The capture field's whole value, after the browser applied it. */
  pushFieldValue(value: string) {
    this.fieldValue = value;
    if (!value) return;
    this.touch();
  }

  /** Text the capture field is about to insert, taken from `beforeinput`. */
  pushFieldInsert(text: string) {
    if (!text) return;
    this.fieldInserts += text;
    this.touch();
  }

  /** A complete payload that arrived in one piece; nothing more is coming. */
  pushAtomic(text: string, source: WedgeSource = 'paste') {
    if (!text) return;
    this.reset();
    this.startedAt = this.now();
    if (source === 'field') this.fieldValue = text;
    else if (source === 'keys') this.keys = text;
    else this.fieldInserts = text;
    this.flush('atomic', source);
  }

  /** Enter or Tab closed the burst. */
  pushTerminator(kind: 'enter' | 'tab') {
    if (!this.isAssembling) return;
    this.flush(kind);
  }

  flush(terminator: WedgeScan['terminator'], forcedSource?: WedgeSource) {
    const cancel = this.options.cancel || ((handle: number) => window.clearTimeout(handle));
    if (this.timer !== null) {
      cancel(this.timer);
      this.timer = null;
    }

    const best = chooseBestReading([
      { value: this.fieldValue, source: 'field' },
      { value: this.fieldInserts, source: forcedSource === 'paste' ? 'paste' : 'field' },
      { value: this.keys, source: 'keys' }
    ]);
    const startedAt = this.startedAt;
    this.reset();
    this.options.onBurstEnd?.();

    const now = this.now();
    const report = (rejected: WedgeRejection) =>
      this.options.onBurstResult?.({
        data: best?.value || '',
        source: forcedSource || best?.source || 'keys',
        terminator,
        rejected
      });

    if (!best) {
      report('empty');
      return;
    }

    // A burst nobody closed has to prove itself on length; a scanner that sent Enter has
    // already told us the payload is complete, however short it is.
    if (terminator === 'quiet' && best.value.length < this.options.minLength) {
      report('too-short');
      return;
    }

    const emittedKey = withoutControls(best.value);
    if (
      emittedKey === this.lastEmittedKey &&
      now - this.lastEmittedAt < this.options.duplicateWindowMs
    ) {
      report('duplicate');
      return;
    }
    this.lastEmittedKey = emittedKey;
    this.lastEmittedAt = now;

    report(null);
    this.options.onScan({
      data: best.value,
      source: forcedSource || best.source,
      terminator,
      durationMs: startedAt ? now - startedAt : 0,
      hasSeparators: best.value.includes('\u001d')
    });
  }

  reset() {
    const cancel = this.options.cancel || ((handle: number) => window.clearTimeout(handle));
    if (this.timer !== null) {
      cancel(this.timer);
      this.timer = null;
    }
    this.keys = '';
    this.fieldValue = '';
    this.fieldInserts = '';
    this.startedAt = 0;
  }

  dispose() {
    this.reset();
  }
}

/**
 * The separator characters DataWedge sends as CTRL key combinations when "Send Control Characters
 * as Events" is on, taken from its ASCII Control Character Table. Only the separators are listed:
 * CTRL + `]` is GS, which divides GS1 application identifiers and is the one that has to survive.
 *
 * CTRL + a letter is deliberately absent. Those encode ASCII 1–26, which GS1's encodable
 * character set excludes and no real barcode carries, whereas swallowing them would mean
 * intercepting every CTRL + letter shortcut on a desktop for nothing.
 */
const CONTROL_BY_KEY: Record<string, string> = {
  '[': '\u001b',
  '\\': '\u001c',
  ']': '\u001d',
  '^': '\u001e',
  _: '\u001f'
};

/** Layouts that report the physical key rather than the character it produced. */
const CONTROL_BY_CODE: Record<string, string> = {
  BracketLeft: '\u001b',
  Backslash: '\u001c',
  BracketRight: '\u001d'
};

/** Turns a key event into the character it stands for, or null when it is not barcode data. */
export const characterFromKeyEvent = (event: {
  key: string;
  code?: string;
  ctrlKey?: boolean;
  altKey?: boolean;
  metaKey?: boolean;
}): string | null => {
  const { key, code, ctrlKey, altKey, metaKey } = event;
  if (metaKey || altKey) return null;
  if (ctrlKey) {
    return CONTROL_BY_KEY[key] ?? (code ? CONTROL_BY_CODE[code] ?? null : null);
  }
  return key.length === 1 ? key : null;
};
